import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { BlogSchemas, type UpdateBlog } from "@workspace/contracts/blogs";
import type { SupportedLanguages } from "@workspace/contracts/language";
import { stripHtml } from "@workspace/core/strip-html";
import { useState } from "react";
import { toast } from "sonner";
import { orpc } from "@/api/orpc";
import { useAppForm } from "@/components/form-inputs";
import { AutoFillAltText } from "@/components/form-inputs/auto-fill-alt-text";
import { LanguageSelector } from "@/components/language-selector";
import { SeoDescriptionField, SeoTitleField } from "@/features/blogs/seo-fields";
import { SlugField } from "@/features/common/slug-field";
import { SeoUrlPreview } from "@/features/common/seo-url-preview";
import { Main } from "@/layout/main";
import { getError } from "@/utils/error";

export const Route = createFileRoute("/_authenticated/blogs/$blogId/update")({
  component: RouteComponent,
  loader: async ({ context: { orpc, queryClient }, params }) => {
    const blog = await queryClient.ensureQueryData(
      orpc.admin.blogs.findById.queryOptions({ input: { id: params.blogId } }),
    );

    return { blog };
  },
});

function RouteComponent() {
  const { blog } = Route.useLoaderData();

  const queryClient = useQueryClient();
  const navigate = Route.useNavigate();
  const [lang, setLang] = useState<SupportedLanguages>("en");

  const { mutateAsync } = useMutation(orpc.admin.blogs.update.mutationOptions());

  const form = useAppForm({
    defaultValues: {
      id: blog.id,
      slug: blog.slug,
      published: blog.published,
      translations: blog.translations,
    } as UpdateBlog,
    validators: {
      onSubmit: BlogSchemas.update,
    },
    onSubmit: async ({ value }) => {
      try {
        await mutateAsync(value);
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: orpc.admin.blogs.list.key({ type: "query" }) }),
          queryClient.invalidateQueries({
            queryKey: orpc.admin.blogs.findById.key({ type: "query", input: { id: blog.id } }),
          }),
          queryClient.invalidateQueries({ queryKey: orpc.blogs.list.key({ type: "query" }) }),
        ]);
        toast.success("Blog was updated successfully!");
        navigate({ to: "/blogs" });
      } catch (error) {
        const msg = getError(error, "Failed to update blog");
        toast.error(msg);
      }
    },
    onSubmitInvalid: ({ formApi }) => {
      console.log(formApi.getAllErrors());
    },
  });

  const onSubmitHandler = (e: React.SubmitEvent) => {
    e.preventDefault();
    form.handleSubmit();
  };

  return (
    <Main>
      <h1 className="mb-2 text-lg">Update Blog</h1>

      <form onSubmit={onSubmitHandler} className="space-y-4">
        <div className="flex w-full items-center justify-between">
          <LanguageSelector lang={lang} setLang={setLang} />
        </div>

        <form.Subscribe selector={(state) => ({ title: state.values.translations[lang].title, altText: state.values.translations[lang].altText ?? "" })}>
          {({ title, altText }) => (
            <AutoFillAltText
              title={title}
              altText={altText}
              setAltText={(value) => form.setFieldValue(`translations.${lang}.altText`, value)}
            />
          )}
        </form.Subscribe>

        <form.AppField name={`translations.${lang}.title`}>
          {(titleField) => <titleField.TextField label="Title" placeholder="Top 10 things that didn't happen!" />}
        </form.AppField>

        <form.Subscribe selector={(state) => ({ title: state.values.translations[lang].title, content: state.values.translations[lang].content })}>
          {({ title, content }) => (
            <div className="space-y-4 rounded-lg border p-4">
              <p className="font-medium">SEO</p>
              <form.AppField name={`translations.${lang}.metaTitle`}>
                {() => (
                  <SeoTitleField
                    description="Defaults to the article title. Enter a value to override it."
                    dir={lang === "ar" || lang === "fa" ? "rtl" : "ltr"}
                    fallback={title}
                    label="SEO Title"
                  />
                )}
              </form.AppField>
              <form.AppField name={`translations.${lang}.metaDescription`}>
                {() => (
                  <SeoDescriptionField
                    description="Defaults to the first 160 characters of the article content. Enter a value to override it."
                    dir={lang === "ar" || lang === "fa" ? "rtl" : "ltr"}
                    fallback={stripHtml(content).slice(0, 160)}
                    label="SEO Description"
                  />
                )}
              </form.AppField>
            </div>
          )}
        </form.Subscribe>

        <form.Subscribe selector={(state) => ({ slug: state.values.translations[lang].slug, title: state.values.translations[lang].title })}>
          {({ slug, title }) => (
            <>
              <form.AppField name={`translations.${lang}.slug`}>
                {(field) => <SlugField locale={lang} name={title} onBlur={field.handleBlur} onChange={field.handleChange} value={field.state.value} />}
              </form.AppField>
              <SeoUrlPreview code={blog.code} locale={lang} slug={slug} title={title} type="blog" />
            </>
          )}
        </form.Subscribe>
        <p className="-mt-2 text-muted-foreground text-sm">
          Editing this slug preserves the previous URL and creates a permanent redirect to the new URL.
        </p>

        <form.AppField name={`translations.${lang}.keywords`}>
          {(field) => <field.TagField label="Keywords" />}
        </form.AppField>

        <form.AppField name={`translations.${lang}.publicationStatus`}>
          {(field) => <field.SelectField label="Translation publication status" options={[{ label: "Draft", value: "draft" }, { label: "Published", value: "published" }]} />}
        </form.AppField>

        <form.AppField name={`translations.${lang}.content`}>
          {(contentField) => (
            <contentField.EditorField
              direction={lang === "ar" || lang === "fa" ? "rtl" : "ltr"}
              label="Content"
              placeholder="Top 10 things that didn't happen!"
              preview
            />
          )}
        </form.AppField>

        <form.AppField name={`translations.${lang}.image`}>
          {(field) => <field.ImageField label="Image" />}
        </form.AppField>

        <form.AppField name={`translations.${lang}.altText`}>
          {(field) => <field.TextField label="Alt Text (auto-filled from the title)" type="text" />}
        </form.AppField>

        <div className="mt-8 flex items-center justify-end gap-6">
          <div>
            <form.AppField name="published">{(field) => <field.CheckboxField label="Published" />}</form.AppField>
          </div>

          <form.AppForm>
            <form.SubmitButton>Submit</form.SubmitButton>
          </form.AppForm>
        </div>
      </form>
    </Main>
  );
}
